import { Button } from '@/components/Button/Button';
import { Checkbox } from '@/components/Checkbox/Checkbox';
import { RadioButton } from '@/components/RadioButton/RadioButton';
import { PromoCheckoutDomainHint } from '@/components/PromoCheckoutDomainHint/PromoCheckoutDomainHint';
import { formatPromoDiscount } from '@/components/PromoCodeFooterLabel/formatPromoDiscount';
import { TermsAndConditionsModal } from '@/components/TermsAndConditionsModal';
import { useA2aPayment } from '@/hooks/useA2aPayment';
import { usePaymentSuccessCleanup } from '@/hooks/usePaymentSuccessCleanup';
import {
  clearCheckoutUrl,
  getCheckoutSession,
  getCheckoutUrl,
  ordersService,
  setCheckoutUrl,
} from '@/services/orders.service';
import { siteContentService } from '@/services/site-content.service';
import {
  isPromoNotActiveReason,
  isPromoNotFoundReason,
  isPromoRedeemedReason,
} from '@/utils/promo-inactive-reason';
import { classNames } from '@/utils/classNames';
import { formatDomainList } from '@/utils/formatDomainList';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styles from './PaymentPage.module.css';
import { usePayment } from './usePayment';

const visaIcon = new URL('../assets/icons/visa.svg', import.meta.url).href;
const mastercardIcon = new URL('../assets/icons/mastercard.svg', import.meta.url).href;
const applePayIcon = new URL('../assets/icons/applepay.svg', import.meta.url).href;
const googlePayIcon = new URL('../assets/icons/googlepay.svg', import.meta.url).href;

const CARD_PAYMENT_METHOD = {
  label: 'Visa or Mastercard',
  priceLabel: (amount: string) => amount,
  logos: [visaIcon, mastercardIcon, applePayIcon, googlePayIcon],
};

const MIN_CHECKOUT_TOTAL_CENTS = 50;

function calculateA2aMaxPoints(
  balance: number | null,
  totalInCents: number,
  maxPercent: number,
): number {
  if (balance == null || totalInCents <= 0) {
    return 0;
  }

  const normalizedPercent = Math.max(0, Math.min(100, Math.trunc(maxPercent)));
  const maxByBookingTotal = (totalInCents * normalizedPercent) / 100 / 100;
  const maxByMinimumRemaining = Math.max(0, (totalInCents - MIN_CHECKOUT_TOTAL_CENTS) / 100);
  return Math.floor(Math.min(balance, maxByBookingTotal, maxByMinimumRemaining) * 100) / 100;
}

function formatPrice(cents: number, currency: string): string {
  const value = (cents / 100).toFixed(2);
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(value));
}

function CloseIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function isValidLoginEmail(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function A2aLoginAlertIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden className={styles.a2aLoginAlertIcon}>
      <path
        d="M12 9v4M12 17h.01M10.3 3.2L2.5 18.1c-.4.7.1 1.6.9 1.6h17.2c.8 0 1.3-.9.9-1.6L13.7 3.2c-.4-.7-1.4-.7-1.8 0z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function A2aLoginModal({
  isOpen,
  onClose,
  onLogin,
  loginError,
  onClearLoginError,
}: {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<boolean>;
  loginError: string | null;
  onClearLoginError: () => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailValidationError, setShowEmailValidationError] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!password) return;

      const trimmed = email.trim();
      if (!isValidLoginEmail(trimmed)) {
        setShowEmailValidationError(true);
        return;
      }

      setShowEmailValidationError(false);
      setSubmitting(true);
      const ok = await onLogin(trimmed, password);
      setSubmitting(false);
      if (ok) onClose();
    },
    [email, password, onLogin, onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setSubmitting(false);
      setShowPassword(false);
      setShowEmailValidationError(false);
    }
  }, [isOpen]);

  const handleBackdropMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const onEmailBlur = useCallback(() => {
    const t = email.trim();
    if (t.length === 0) return;
    if (!isValidLoginEmail(t)) setShowEmailValidationError(true);
  }, [email]);

  const onEmailChange = useCallback(
    (v: string) => {
      setEmail(v);
      onClearLoginError();
      if (isValidLoginEmail(v)) setShowEmailValidationError(false);
    },
    [onClearLoginError],
  );

  const onPasswordChange = useCallback(
    (v: string) => {
      setPassword(v);
      onClearLoginError();
    },
    [onClearLoginError],
  );

  if (!isOpen) return null;

  const emailInvalid =
    showEmailValidationError && email.trim().length > 0 && !isValidLoginEmail(email.trim());
  const canSubmit = isValidLoginEmail(email) && password.length > 0 && !submitting;

  return createPortal(
    <div className={styles.modalBackdrop} onMouseDown={handleBackdropMouseDown} role="dialog" aria-modal="true">
      <div
        className={styles.a2aModal}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className={styles.a2aModalTop}>
          <div className={styles.a2aModalTopText}>
            <h3 className={styles.a2aModalTitle}>Log in to your Alps2Alps account</h3>
            <p className={styles.a2aModalSubtitle}>
              Use the email from your Alps2Alps booking to check and use your points.
            </p>
          </div>
          <button type="button" className={styles.a2aModalClose} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <form className={styles.a2aModalForm} noValidate onSubmit={(e) => { void handleSubmit(e); }}>
          <div className={styles.a2aModalFields}>
            <div className={styles.a2aLoginField}>
              <div className={styles.a2aLoginLabelRow}>
                <span>Email</span>
                <span className={styles.a2aLoginRequired} aria-hidden>
                  *
                </span>
              </div>
              <div
                className={classNames(
                  styles.a2aLoginInputOuter,
                  emailInvalid ? styles.a2aLoginInputOuterError : '',
                )}
              >
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  className={styles.a2aLoginInputInner}
                  value={email}
                  onChange={(e) => { onEmailChange(e.target.value); }}
                  onBlur={onEmailBlur}
                  aria-invalid={emailInvalid}
                  aria-describedby={emailInvalid ? 'a2a-email-error' : undefined}
                />
              </div>
              {emailInvalid && (
                <span id="a2a-email-error" className={styles.a2aLoginCaptionError} role="alert">
                  Please enter a valid email address
                </span>
              )}
            </div>

            <div className={styles.a2aInputPasswordGroup}>
              <div className={styles.a2aLoginField}>
                <div className={styles.a2aLoginLabelRow}>
                  <span>Password</span>
                  <span className={styles.a2aLoginRequired} aria-hidden>
                    *
                  </span>
                </div>
                <div className={styles.a2aLoginInputOuter}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={classNames(styles.a2aLoginInputInner, styles.a2aLoginInputInnerWithToggle)}
                    value={password}
                    onChange={(e) => { onPasswordChange(e.target.value); }}
                  />
                  <button
                    type="button"
                    className={styles.a2aInputToggle}
                    onClick={() => { setShowPassword((v) => !v); }}
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                      {showPassword ? (
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      ) : (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </>
                      )}
                      {showPassword && <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />}
                    </svg>
                  </button>
                </div>
              </div>
              <div className={styles.a2aPasswordHint}>
                <span>Don&apos;t know or forgot your password?</span>
                <button type="button" className={styles.a2aResetLink}>Reset it</button>
              </div>
            </div>
          </div>

          {loginError != null ? (
            <div className={styles.a2aLoginAlert} role="alert">
              <A2aLoginAlertIcon />
              <p className={styles.a2aLoginAlertText}>{loginError}</p>
            </div>
          ) : null}

          <button
            type="submit"
            className={classNames(styles.a2aModalPrimaryBtn, !canSubmit ? styles.a2aModalPrimaryBtnDisabled : '')}
            disabled={!canSubmit}
          >
            {submitting ? 'Logging in...' : 'Log in'}
          </button>
        </form>
      </div>
    </div>,
    document.body,
  );
}

/** Customer-facing reason when MAM Engine allows nothing on this order. */
function describeBlockedReason(blockedBy: string | null, minOrderValue: number | null): string | null {
  switch (blockedBy) {
    case null:
      return null;
    case 'promo_conflict':
      return 'Loyalty points cannot be combined with a promo code.';
    case 'below_min_order':
      return minOrderValue != null
        ? `Loyalty points can be used on orders from ${String(minOrderValue)} € upwards.`
        : 'This order is below the minimum amount for using loyalty points.';
    case 'member_not_found':
      return 'No loyalty account was found for this email yet.';
    case 'currency_unsupported':
      return 'Loyalty points can only be used on orders priced in euro.';
    case 'card_minimum':
      return 'The remaining card amount would be below the minimum card charge.';
    case 'no_redeemable_balance':
      return 'No loyalty points can be used on this order right now.';
    default:
      return 'Loyalty points cannot be used on this order.';
  }
}

function LoyaltyPointsModal({
  isOpen,
  onClose,
  balance,
  maxPoints,
  maxPercent,
  engine,
  onApply,
  appliedPoints,
  onCancel,
}: {
  isOpen: boolean;
  onClose: () => void;
  balance: number;
  maxPoints: number;
  maxPercent: number;
  /** Limit comes from MAM Engine (per-order quote) rather than the local percent setting. */
  engine: boolean;
  onApply: (points: number) => void;
  appliedPoints: number | null;
  onCancel: () => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setInputValue(appliedPoints != null ? String(appliedPoints) : '');
    }
  }, [isOpen, appliedPoints]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleBackdropMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/[^0-9.]/g, '');
      const dotCount = (raw.match(/\./g) ?? []).length;
      if (dotCount > 1) return;
      if (!raw) {
        setInputValue('');
        return;
      }
      if (raw === '.' || raw.endsWith('.')) {
        setInputValue(raw);
        return;
      }
      const num = Number(raw);
      if (Number.isNaN(num)) return;
      if (num > maxPoints) {
        setInputValue(String(maxPoints));
        return;
      }
      setInputValue(raw);
    },
    [maxPoints],
  );

  const handleApply = useCallback(() => {
    const num = Number(inputValue);
    if (num >= 0 && num <= maxPoints) {
      onApply(num);
      onClose();
    }
  }, [inputValue, maxPoints, onApply, onClose]);

  const handleCancelPoints = useCallback(() => {
    setInputValue('');
    onCancel();
  }, [onCancel]);

  if (!isOpen) return null;

  const trimmedInputValue = inputValue.trim();
  const numericValue = Number(trimmedInputValue);
  const hasValidInput = trimmedInputValue.length > 0 && Number.isFinite(numericValue);
  const canApply = hasValidInput && numericValue >= 0 && numericValue <= maxPoints;
  const applyLabel = canApply ? `Apply ${trimmedInputValue} points` : 'Apply points';

  return createPortal(
    <div className={styles.modalBackdrop} onMouseDown={handleBackdropMouseDown} role="dialog" aria-modal="true">
      <div
        className={styles.a2aModal}
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <div className={styles.a2aModalTop}>
          <div className={styles.a2aModalTopText}>
            <h3 className={styles.a2aModalTitle}>Use loyalty points</h3>
            <p className={styles.a2aModalSubtitle}>
              Balance: {balance} MAM&apos;s. 1 MAM = 1 &euro;.<br />
              Max for this booking: {maxPoints % 1 === 0 ? maxPoints : maxPoints.toFixed(2)} MAMs
              {engine ? '' : ` (${String(maxPercent)}%)`}
            </p>
          </div>
          <button type="button" className={styles.a2aModalClose} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className={styles.a2aModalForm}>
          <div className={classNames(styles.a2aInputBox, styles.a2aInputBoxFilled)}>
            <span className={styles.a2aInputLabel}>Loyalty Points (MAMs)</span>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              className={styles.a2aInput}
              placeholder=""
              value={inputValue}
              onChange={handleInputChange}
            />
            {inputValue && (
              <button
                type="button"
                className={styles.a2aPointsCancelBtn}
                onClick={handleCancelPoints}
              >
                Cancel
              </button>
            )}
          </div>

          <button
            type="button"
            className={classNames(styles.a2aModalPrimaryBtn, !canApply ? styles.a2aModalPrimaryBtnDisabled : '')}
            disabled={!canApply}
            onClick={handleApply}
          >
            {applyLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export const PaymentPage = () => {
  const navigate = useNavigate();
  const {
    checkout,
    loading,
    error,
    hasStripeRedirectParams,
    returnCase,
    passItems,
    subtotal,
    grandTotal,
    promoDiscountAmount,
    currency,
    promoCode,
    promoCodeInfo,
    refetchCheckout,
  } = usePayment();

  // The order id lets MAM Engine quote the redeemable limit against this order (value, promo code).
  const checkoutSessionOrderId = getCheckoutSession()?.orderId;
  const a2a = useA2aPayment(checkoutSessionOrderId);
  const a2aApplyPoints = a2a.applyPoints;
  const a2aClearPoints = a2a.clearPoints;
  const a2aAppliedPoints = a2a.appliedPoints;
  const a2aEngine = a2a.engine;
  const a2aMaxRedeemable = a2a.maxRedeemable;

  usePaymentSuccessCleanup(returnCase);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [pointsModalOpen, setPointsModalOpen] = useState(false);
  const [a2aMaxPercentSetting, setA2aMaxPercentSetting] = useState(25);

  useEffect(() => {
    void siteContentService
      .getPublicPricingSettings()
      .then((settings) => {
        const percent = Number.isFinite(settings.mamMaxPercent)
          ? Math.max(0, Math.min(100, Math.trunc(settings.mamMaxPercent)))
          : 25;
        setA2aMaxPercentSetting(percent);
      })
      .catch(() => {
        setA2aMaxPercentSetting(25);
      });
  }, []);

  const checkoutUrl = getCheckoutUrl();
  const mainAmountStr = formatPrice(grandTotal, currency);

  // MAM Engine customers: the engine's per-order limit is the authority (already card-minimum safe).
  // Legacy customers: the local percent setting and the card-payment minimum, as before.
  const a2aMaxPoints = a2aEngine
    ? Math.max(0, a2aMaxRedeemable ?? 0)
    : calculateA2aMaxPoints(a2a.balance, grandTotal, a2aMaxPercentSetting);
  const isA2aEnabled = a2aMaxPercentSetting > 0;
  const a2aMaxPercent = grandTotal > 0
    ? Math.round(a2aMaxPoints / (grandTotal / 100) * 100)
    : 0;
  const appliedPointsCents = isA2aEnabled && a2aAppliedPoints != null
    ? Math.round(a2aAppliedPoints * 100)
    : 0;
  const effectiveTotal = grandTotal - appliedPointsCents;
  const effectiveTotalStr = formatPrice(effectiveTotal, currency);

  const displayTotal = isA2aEnabled && a2aAppliedPoints != null && a2aAppliedPoints > 0
    ? effectiveTotalStr
    : mainAmountStr;

  const [autoApplyRequested, setAutoApplyRequested] = useState(true);

  const handleLoginSuccess = useCallback(
    async (email: string, password: string) => {
      const ok = await a2a.login(email, password);
      if (ok) {
        setAutoApplyRequested(true);
        setLoginModalOpen(false);
      }
      return ok;
    },
    [a2a],
  );

  useEffect(() => {
    if (isA2aEnabled) {
      return;
    }

    a2aClearPoints();
    setLoginModalOpen(false);
    setPointsModalOpen(false);
  }, [isA2aEnabled, a2aClearPoints]);

  useEffect(() => {
    if (!autoApplyRequested) return;

    if (!isA2aEnabled) {
      setAutoApplyRequested(false);
      return;
    }

    // Wait for checkout totals to be loaded before deciding max MAM to apply.
    if (!checkout || grandTotal <= 0) {
      return;
    }

    if (a2a.loading) {
      return;
    }

    if (!a2a.authenticated || a2a.balance == null) {
      setAutoApplyRequested(false);
      return;
    }

    if (a2aAppliedPoints != null && a2aAppliedPoints > 0) {
      setAutoApplyRequested(false);
      return;
    }

    if (a2aMaxPoints > 0) {
      a2aApplyPoints(a2aMaxPoints);
    }
    setAutoApplyRequested(false);
  }, [
    autoApplyRequested,
    checkout,
    grandTotal,
    a2a.loading,
    a2a.authenticated,
    a2a.balance,
    a2aAppliedPoints,
    a2aMaxPoints,
    a2aApplyPoints,
    isA2aEnabled,
  ]);

  useEffect(() => {
    if (!isA2aEnabled) {
      return;
    }

    if (a2a.loading || !a2a.authenticated || a2a.balance == null) {
      return;
    }

    if (a2aAppliedPoints == null || a2aAppliedPoints <= 0) {
      return;
    }

    const clampedPoints = Math.min(a2aAppliedPoints, a2aMaxPoints);

    if (clampedPoints === a2aAppliedPoints) {
      return;
    }

    if (clampedPoints > 0) {
      a2aApplyPoints(clampedPoints);
      return;
    }

    a2aClearPoints();
  }, [
    isA2aEnabled,
    a2a.loading,
    a2a.authenticated,
    a2a.balance,
    a2aAppliedPoints,
    a2aMaxPoints,
    a2aApplyPoints,
    a2aClearPoints,
  ]);

  // Record the logged-in A2A account on the order so that a paid order can earn MAM, then match the
  // price to the login: fixed price for a listed account, regular price after logout or another
  // account. A change comes with a new card checkout at the new price.
  useEffect(() => {
    if (a2a.loading) {
      return;
    }
    const session = getCheckoutSession();
    if (!session) {
      return;
    }
    const syncLoyaltyPrice = async () => {
      try {
        const result = await ordersService.syncLoyaltyPrice(session.orderId, session.checkoutToken);
        if (!result.changed) {
          return;
        }
        if (result.checkoutUrl) {
          setCheckoutUrl(result.checkoutUrl);
        }
        await refetchCheckout();
        if (a2a.authenticated) {
          await a2a.refreshBalance(session.orderId);
        }
      } catch {
        // the price stays as it is
      }
    };
    if (isA2aEnabled && a2a.authenticated) {
      void a2a.attachOrder(session.orderId, session.checkoutToken).then(syncLoyaltyPrice);
      return;
    }
    void syncLoyaltyPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isA2aEnabled, a2a.loading, a2a.authenticated, a2a.email]);

  // Back from checkout without paying: return MAM redeemed up front on MAM Engine (legacy: no-op).
  const cancelReleaseRef = useRef(false);
  useEffect(() => {
    if (returnCase !== 'cancel' || cancelReleaseRef.current) {
      return;
    }
    cancelReleaseRef.current = true;
    const session = getCheckoutSession();
    if (!session) {
      return;
    }
    void a2a.releasePoints(session.orderId, session.checkoutToken).then(() => a2a.refreshBalance(session.orderId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnCase]);

  const handleCancelPoints = useCallback(() => {
    a2aClearPoints();
    if (a2aEngine) {
      const session = getCheckoutSession();
      if (session) {
        void a2a.releasePoints(session.orderId, session.checkoutToken);
      }
    }
  }, [a2a, a2aClearPoints, a2aEngine]);

  const handleLoyaltyBannerClick = useCallback(() => {
    if (a2a.authenticated) {
      setPointsModalOpen(true);
      return;
    }
    setLoginModalOpen(true);
  }, [a2a.authenticated]);

  const handleChangeAccount = useCallback(() => {
    void a2a.logout();
  }, [a2a]);

  const handlePayNow = useCallback(() => {
    setPayError(null);

    if (isA2aEnabled && a2a.authenticated && a2aAppliedPoints != null && a2aAppliedPoints > 0) {
      const session = getCheckoutSession();
      if (!session) {
        setPayError('Checkout session not found. Please go back to booking details.');
        return;
      }
      void a2a.pay(session.orderId, session.checkoutToken, a2aAppliedPoints).then((outcome) => {
        if (outcome === 'paid') {
          // MAM Engine covered the whole order: no card step, the order is already paid.
          clearCheckoutUrl();
          void navigate(`/payment?orderId=${String(session.orderId)}&session_id=mam`, { replace: true });
        }
      });
      return;
    }

    const url = checkoutUrl;
    if (!url) {
      setPayError('Payment link not found. Please go back to booking details and try again.');
      return;
    }
    clearCheckoutUrl();
    window.location.assign(url);
  }, [checkoutUrl, a2a, a2aAppliedPoints, isA2aEnabled, navigate]);

  if (hasStripeRedirectParams) {
    const customerEmail = checkout?.customerEmail?.trim();

    return (
      <main className={styles.returnRoot}>
        <section className={styles.returnCard}>
          <div className={styles.returnCenter}>
            {returnCase === 'success' && (
              <>
                <h1 className={styles.returnSuccessTitle}>🎉 You&apos;ve successfully booked Ski Passes!</h1>
                <p className={styles.returnSuccessLead}>
                  {customerEmail
                    ? (
                      <>
                        Your voucher will be sent to: <a href={`mailto:${customerEmail}`}>{customerEmail}</a>.
                      </>
                    )
                    : 'You will receive a confirmation email shortly.'}
                </p>
                <p className={styles.returnSuccessBody}>
                  Please bring the voucher code with you when collecting your ski passes. You can pick
                  them up either at the ticket machine near the lifts, or at the resort&apos;s ticket office.
                </p>
                <div className={styles.returnActions}>
                  <Button
                    type="button"
                    variant="solid"
                    size="large"
                    className={styles.returnActionButton}
                    onClick={() => {
                      void navigate('/');
                    }}
                  >
                    Back to home
                  </Button>
                </div>
              </>
            )}

            {returnCase === 'pending' && (
              <p className={styles.returnMessage}>
                We&apos;re confirming your payment. This page updates when the payment is finished.
              </p>
            )}

            {returnCase === 'failed' && (
              <p className={classNames(styles.returnMessage, styles.returnCancel)}>
                The payment was not completed. You can try again when ready.
              </p>
            )}

            {returnCase === 'cancel' && (
              <p className={classNames(styles.returnMessage, styles.returnCancel)}>
                Payment was cancelled. You can try again when ready.
              </p>
            )}
          </div>
        </section>
      </main>
    );
  }

  if (loading && !checkout) {
    return (
      <div className={styles.content}>
        <p className={styles.message}>Loading payment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.content}>
        <p className={styles.error}>{error}</p>
      </div>
    );
  }

  if (!checkout) {
    return (
      <div className={styles.content}>
        <p className={styles.message}>No checkout session. Please start from trip details.</p>
      </div>
    );
  }

  const canPay = termsAccepted && (
    isA2aEnabled && a2aAppliedPoints != null && a2aAppliedPoints > 0
      ? !a2a.paying
      : Boolean(checkoutUrl)
  );

  const isPromoActive = promoCodeInfo?.status === 'active';
  const isPromoEffectivelyApplied = isPromoActive && promoDiscountAmount > 0;
  const promoTypeLabel = formatPromoDiscount(promoCodeInfo?.type, promoCodeInfo?.value);
  let promoInactiveReason = promoCodeInfo?.inactiveReason;
  const shouldHidePromoRow =
    !promoCode
    || isPromoNotFoundReason(promoInactiveReason)
    || isPromoRedeemedReason(promoInactiveReason)
    || isPromoNotActiveReason(promoInactiveReason);
  const shouldShowPromoRow = !shouldHidePromoRow;
  const isDomainMismatchInactive = promoCodeInfo?.domainRestrictionInvalid === true;
  const isPartialDomainPromo = promoCodeInfo?.domainRestrictionPartial === true;
  const hasDomainRestrictionHint = isPartialDomainPromo || isDomainMismatchInactive;
  const shouldShowInactiveReasonHint = !isPromoActive && !hasDomainRestrictionHint && Boolean(promoInactiveReason) ||
    isDomainMismatchInactive || isPartialDomainPromo;
  const restrictedNames = promoCodeInfo?.restrictedDomainNames ?? [];
  const domainListLabel = formatDomainList(restrictedNames);
  if (restrictedNames.length > 0){
    promoInactiveReason = isPartialDomainPromo ? `This promo code is valid only for ${domainListLabel}. The discount was applied only to ski passes for these domains.` :
      `This promo code is valid only for ${domainListLabel}. None of the ski passes in your order match these restrictions.`;
  }
  const showOrderSubtotal = shouldShowPromoRow || (isA2aEnabled && a2aAppliedPoints != null && a2aAppliedPoints > 0);

  const combinedError = payError ?? a2a.payError;

  const termsBlock = (
    <div className={styles.termsRow}>
      <Checkbox
        checked={termsAccepted}
        onChange={(e) => {
          setTermsAccepted(e.target.checked);
        }}
        aria-label="I agree to the Terms and Conditions"
      />
      <span className={styles.termsText}>I agree to the </span>
      <button
        type="button"
        className={styles.termsLink}
        onClick={() => {
          setTermsModalOpen(true);
        }}
      >
        Terms and Conditions
      </button>
    </div>
  );

  const payButtonEl = (
    <Button
      variant="solid"
      size="large"
      disabled={!canPay}
      loading={a2a.paying}
      onClick={handlePayNow}
      className={styles.fullWidthButton}
    >
      Pay now {displayTotal}
    </Button>
  );

  const loyaltySection = (() => {
    if (!isA2aEnabled) {
      return null;
    }

    if (a2a.authenticated && a2aAppliedPoints != null && a2aAppliedPoints > 0) {
      return (
        <div
          className={styles.loyaltyRow}
          onClick={() => { setPointsModalOpen(true); }}
        >
          <div className={styles.loyaltyRowLeft}>
            <span className={styles.loyaltyRowLabel}>
              Loyalty points ({a2aAppliedPoints} MAMs)
            </span>
            <span className={styles.loyaltyRowArrow}>
              <ChevronRightIcon />
            </span>
          </div>
          <span className={styles.loyaltyRowDiscount}>
            -{formatPrice(appliedPointsCents, currency)}
          </span>
        </div>
      );
    }

    if (a2a.authenticated && a2a.balance != null && a2a.balance > 0 && a2aMaxPoints > 0) {
      return (
        <div
          className={styles.loyaltyRow}
          onClick={() => { setPointsModalOpen(true); }}
        >
          <div className={styles.loyaltyRowLeft}>
            <span className={styles.loyaltyRowLabel}>
              Apply loyalty points ({a2aMaxPoints % 1 === 0 ? a2aMaxPoints : a2aMaxPoints.toFixed(2)} MAMs)
            </span>
            <span className={styles.loyaltyRowArrow}>
              <ChevronRightIcon />
            </span>
          </div>
        </div>
      );
    }

    if (a2a.authenticated && !a2a.loading) {
      const blockedReason = a2aEngine ? describeBlockedReason(a2a.blockedBy, a2a.minOrderValue) : null;
      return (
        <button
          type="button"
          className={styles.loyaltyBanner}
          onClick={() => { setLoginModalOpen(true); }}
        >
          <span>
            {blockedReason && a2a.balance != null && a2a.balance > 0
              ? `${blockedReason} Tap to change account.`
              : 'No loyalty points available right now. Tap to change account.'}
          </span>
          <span className={styles.loyaltyBannerArrow}>
            <ChevronRightIcon />
          </span>
        </button>
      );
    }

    if (!a2a.authenticated && !a2a.loading) {
      return (
        <button
          type="button"
          className={styles.loyaltyBanner}
          onClick={handleLoyaltyBannerClick}
        >
          <span>
            Booked a transfer with Alps2Alps?
            <span className={styles.loyaltyBannerLoginLink}>Log in to save with your points</span>
          </span>
          <span className={styles.loyaltyBannerArrow}>
            <ChevronRightIcon />
          </span>
        </button>
      );
    }

    return null;
  })();

  const orderSummary = (
    <>
      {a2a.authenticated && a2a.email && (
        <div className={styles.a2aLoggedInBar}>
          <span className={styles.a2aLoggedInEmail}>Logged in as {a2a.email}</span>
          {' '}
          <button
            type="button"
            className={styles.a2aChangeLink}
            onClick={handleChangeAccount}
          >
            Change
          </button>
        </div>
      )}
      <div className={styles.orderSummaryOuter}>
        <p className={styles.depositNote}>Each ski pass includes a card deposit</p>
        <div className={styles.orderSummaryCard}>
          <div className={styles.passList}>
            {passItems.map((item) => {
              const perPassDiscount = item.discountAmount;
              return (
                <div key={item.slot.index} className={styles.passItem}>
                  <div className={styles.passItemLeft}>
                    <div className={styles.passItemTitle}>
                      <span className={styles.passItemName}>
                        {item.firstName} {item.lastName}
                      </span>
                      <span className={styles.passItemBadge}>{item.slot.ageCategory}</span>
                    </div>
                    <div className={styles.passItemDescription}>
                      <span>{item.slot.domain.name}</span>
                      <span className={styles.passItemBullet}>&bull;</span>
                      <span>{item.days} days</span>
                    </div>
                  </div>
                  <div className={styles.passItemPriceWrap}>
                    <span className={styles.passItemPrice}>
                      {formatPrice(item.unitPrice, item.currency)}
                    </span>
                    {isPromoEffectivelyApplied && perPassDiscount > 0 && (
                      <span className={styles.passItemDiscount}>
                        -{formatPrice(perPassDiscount, item.currency)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className={styles.separator} />

          <div className={styles.totalsSection}>
            {showOrderSubtotal && (
              <div className={styles.subtotalRow}>
                <span className={styles.subtotalLabel}>Subtotal</span>
                <span className={styles.subtotalValue}>{formatPrice(subtotal, currency)}</span>
              </div>
            )}
            {shouldShowPromoRow && (
              <div className={styles.promoRow}>
                <div className={styles.promoRowLeft}>
                  <span className={styles.promoLabel}>
                    Promo code {promoCode}
                    {promoTypeLabel ? ` (${promoTypeLabel})` : ''}
                  </span>
                  {shouldShowInactiveReasonHint && (
                    <div className={styles.promoHintRow}>
                      <PromoCheckoutDomainHint tooltip={promoInactiveReason ?? 'Promo code conditions are not met.'}>
                        Applied only to eligible ski passes
                      </PromoCheckoutDomainHint>
                    </div>
                  )}
                </div>
                {isPromoEffectivelyApplied && (
                  <span className={styles.promoDiscount}>
                    -{formatPrice(promoDiscountAmount, currency)}
                  </span>
                )}
              </div>
            )}

            {loyaltySection}

            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Total</span>
              <span className={styles.totalAmount}>{displayTotal}</span>
            </div>
          </div>

          <div className={styles.buttonWrap}>
            {termsBlock}
            {payButtonEl}
            {combinedError && <p className={styles.error}>{combinedError}</p>}
          </div>

          <TermsAndConditionsModal
            isOpen={termsModalOpen}
            onClose={() => {
              setTermsModalOpen(false);
            }}
          />
        </div>
      </div>
    </>
  );

  return (
    <div className={styles.content}>
      <div className={styles.layout}>
        <section className={styles.paymentOptionsSection}>
          <h4 className={styles.sectionTitle}>Payment details</h4>
          <div className={styles.paymentMethodWrapper}>
            <div className={styles.paymentMethodList}>
              <div className={classNames(styles.paymentMethodOption, styles.paymentMethodOptionSelected)}>
                <RadioButton
                  name="paymentMethod"
                  value="card"
                  checked
                  onChange={() => {}}
                  className={styles.paymentMethodRadio}
                />
                <div className={styles.paymentMethodContent}>
                  <div className={styles.paymentMethodDetailsWrapper}>
                    <span className={styles.paymentMethodLabel}>{CARD_PAYMENT_METHOD.label}</span>
                    <span className={styles.paymentMethodPrice}>
                      <span className={styles.paymentMethodPriceNote}>Pay </span>
                      {CARD_PAYMENT_METHOD.priceLabel(displayTotal)}
                    </span>
                  </div>
                  <div className={styles.paymentMethodLogos}>
                    {CARD_PAYMENT_METHOD.logos.map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt=""
                        className={styles.paymentMethodLogo}
                        aria-hidden
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <aside className={styles.orderSummarySection}>{orderSummary}</aside>
      </div>

      <div className={styles.mobileBottomBar} aria-hidden>
        {termsBlock}
        {payButtonEl}
      </div>

      <A2aLoginModal
        isOpen={isA2aEnabled && loginModalOpen}
        onClose={() => { setLoginModalOpen(false); }}
        onLogin={handleLoginSuccess}
        loginError={a2a.loginError}
        onClearLoginError={a2a.clearLoginError}
      />

      <LoyaltyPointsModal
        isOpen={isA2aEnabled && pointsModalOpen}
        onClose={() => { setPointsModalOpen(false); }}
        balance={a2a.balance ?? 0}
        maxPoints={a2aMaxPoints}
        maxPercent={a2aMaxPercent}
        engine={a2aEngine}
        onApply={a2aApplyPoints}
        appliedPoints={a2aAppliedPoints}
        onCancel={handleCancelPoints}
      />
    </div>
  );
};
